import { useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { 
  Share2, 
  Copy, 
  Check, 
  Mail, 
  Twitter, 
  Facebook, 
  Linkedin,
  MessageCircle,
  Link2
} from 'lucide-react';

const FRONTEND_URL = window.location.origin;

export default function ShareResults({ 
  surveyId, 
  surveyTitle, 
  totalResponses = 0,
  dominantSentiment = 'neutral',
  topConcern = '',
  type = 'survey', // 'survey' or 'report'
  countryName = 'Nigeria' // Dynamic country name
}) {
  const [copied, setCopied] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState({ to: '', subject: '', body: '' });

  // Dynamic report name based on country
  const reportName = `Citizen Voice ${countryName}`;
  
  const shareUrl = `${FRONTEND_URL}/results/${surveyId}`;
  
  const shareText = `📊 ${reportName}: "${surveyTitle}" - ${totalResponses} citizens have shared their views. Dominant sentiment: ${dominantSentiment}. ${topConcern ? `Top concern: ${topConcern}` : ''} See the results:`;
  
  // Dynamic hashtags based on country
  const hashtags = `CitizenVoice${countryName.replace(/\s/g, '')},${countryName.replace(/\s/g, '')},Governance,CivicTech,Africa`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
    window.open(url, '_blank');
  };

  const openEmailComposer = () => {
    setEmail({
      to: '',
      subject: `Citizen Voice Nigeria: Survey Results - ${surveyTitle}`,
      body: `Hello,\n\nI wanted to share the results of a recent citizen survey from Citizen Voice Nigeria.\n\n📊 Survey: ${surveyTitle}\n👥 Total Responses: ${totalResponses}\n💭 Dominant Sentiment: ${dominantSentiment}\n${topConcern ? `⚠️ Top Concern: ${topConcern}\n` : ''}\n🔗 View Full Results: ${shareUrl}\n\nThis data represents anonymous citizen feedback on governance issues in Nigeria.\n\nBest regards`
    });
    setEmailOpen(true);
  };

  const sendEmail = () => {
    const mailtoUrl = `mailto:${email.to}?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
    window.location.href = mailtoUrl;
    setEmailOpen(false);
    toast.success('Email composer opened!');
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Survey Results: ${surveyTitle}`,
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share');
        }
      }
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="share-results-btn">
          <Share2 className="mr-2 h-4 w-4" />
          Share Results
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share Survey Results</DialogTitle>
          <DialogDescription>
            Share "{surveyTitle}" results with others
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Copy Link */}
          <div className="space-y-2">
            <Label>Share Link</Label>
            <div className="flex gap-2">
              <Input 
                value={shareUrl} 
                readOnly 
                className="font-mono text-sm"
              />
              <Button 
                variant="outline" 
                size="icon"
                onClick={copyToClipboard}
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Social Share Buttons */}
          <div className="space-y-2">
            <Label>Share on Social Media</Label>
            <div className="grid grid-cols-4 gap-2">
              <Button 
                variant="outline" 
                className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-blue-50 hover:border-blue-300"
                onClick={shareToTwitter}
                data-testid="share-twitter-btn"
              >
                <Twitter className="h-5 w-5 text-[#1DA1F2]" />
                <span className="text-xs">Twitter</span>
              </Button>
              <Button 
                variant="outline" 
                className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-blue-50 hover:border-blue-300"
                onClick={shareToFacebook}
                data-testid="share-facebook-btn"
              >
                <Facebook className="h-5 w-5 text-[#4267B2]" />
                <span className="text-xs">Facebook</span>
              </Button>
              <Button 
                variant="outline" 
                className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-green-50 hover:border-green-300"
                onClick={shareToWhatsApp}
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="h-5 w-5 text-[#25D366]" />
                <span className="text-xs">WhatsApp</span>
              </Button>
              <Button 
                variant="outline" 
                className="flex flex-col items-center gap-1 h-auto py-3 hover:bg-blue-50 hover:border-blue-300"
                onClick={shareToLinkedIn}
                data-testid="share-linkedin-btn"
              >
                <Linkedin className="h-5 w-5 text-[#0077B5]" />
                <span className="text-xs">LinkedIn</span>
              </Button>
            </div>
          </div>

          {/* Email Share */}
          <div className="space-y-2">
            <Label>Share via Email</Label>
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={openEmailComposer}
              data-testid="share-email-btn"
            >
              <Mail className="mr-2 h-4 w-4" />
              Compose Email
            </Button>
          </div>

          {/* Native Share (Mobile) */}
          {navigator.share && (
            <Button 
              className="w-full"
              onClick={nativeShare}
            >
              <Share2 className="mr-2 h-4 w-4" />
              More Share Options
            </Button>
          )}
        </div>

        {/* Preview */}
        <div className="bg-muted/50 rounded-lg p-3 text-sm">
          <p className="font-medium mb-1">Preview:</p>
          <p className="text-muted-foreground text-xs">{shareText}</p>
        </div>
      </DialogContent>

      {/* Email Composer Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Compose Email</DialogTitle>
            <DialogDescription>
              Customize and send the survey results via email
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email-to">To</Label>
              <Input 
                id="email-to"
                type="email"
                placeholder="recipient@example.com"
                value={email.to}
                onChange={(e) => setEmail({...email, to: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-subject">Subject</Label>
              <Input 
                id="email-subject"
                value={email.subject}
                onChange={(e) => setEmail({...email, subject: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email-body">Message</Label>
              <Textarea 
                id="email-body"
                rows={8}
                value={email.body}
                onChange={(e) => setEmail({...email, body: e.target.value})}
              />
            </div>
            <Button onClick={sendEmail} className="w-full">
              <Mail className="mr-2 h-4 w-4" />
              Open in Email App
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
